/**
 * EmojiPicker.tsx
 * ─────────────────────────────────────────────────────────────────────────────
 * Orchestrating component.
 *
 * Architecture
 * ┌─ EmojiPicker ─────────────────────────────────────────────────────────────┐
 * │  useEmojiUsage   — localStorage persistence, recent-list derivation       │
 * │  view state      — 'recent' | 'all' tab                                   │
 * │  [ 最近 | 全部 ]  tab row (only when there are recent items)              │
 * │  ┌── EmojiGrid ────────────────────────────────────────────────────────┐  │
 * │  │  virtualised emoji grid (useEmojiVirtualizer + SimpleGrid)          │  │
 * │  └─────────────────────────────────────────────────────────────────────┘  │
 * └───────────────────────────────────────────────────────────────────────────┘
 *
 * Usage
 * ┌─ PC  ─────────────────────────────────────────────────────────────────────┐
 * │  <Popover><Popover.Dropdown><EmojiPicker … /></Popover.Dropdown></Popover> │
 * └───────────────────────────────────────────────────────────────────────────┘
 * ┌─ Mobile ──────────────────────────────────────────────────────────────────┐
 * │  {isEmojiPickerOpen && <EmojiPicker … />}  (inline drawer panel)          │
 * └───────────────────────────────────────────────────────────────────────────┘
 *
 * The component adapts to its container height; callers control sizing via
 * wrappers (Popover.Dropdown width or a plain Box height on mobile).
 */

import { useEffect, useMemo, useState } from 'react';
import { Box, Button, Group, Paper } from '@mantine/core';
import { encodeEmojiPlaceholder } from './placeholder';
import { getEmojiList } from './emojiData';
import { useEmojiUsage } from './useEmojiUsage';
import { EmojiGrid } from './EmojiGrid';

type View = 'recent' | 'all';

export type EmojiPickerProps = {
  onSelect: (placeholder: string) => void;
  /**
   * When used inside a Popover.Dropdown, pass `variant="popover"` so the
   * component renders with a card (Paper) shell.
   * When used as an inline panel (mobile drawer), pass `variant="inline"` to
   * skip the shell — the parent supplies the background.
   */
  variant?: 'popover' | 'inline';
};

export function EmojiPicker({ onSelect, variant = 'popover' }: EmojiPickerProps) {
  const { recentItems, recordUsage } = useEmojiUsage();
  const hasRecent = recentItems.length > 0;
  const [view, setView] = useState<View>(hasRecent ? 'recent' : 'all');

  // Reset to 'all' if the user clears their history
  useEffect(() => {
    if (!hasRecent && view === 'recent') setView('all');
  }, [hasRecent, view]);

  const allEmojiItems = useMemo(
    () => getEmojiList('fluentui').map((codepoints) => ({ packId: 'fluentui' as const, codepoints })),
    [],
  );

  const gridItems = view === 'recent' ? recentItems : allEmojiItems;

  function handleSelect(codepoints: string) {
    recordUsage(codepoints);
    onSelect(encodeEmojiPlaceholder(codepoints));
  }

  const content = (
    <>
      {/* ── View tabs (recent / all) ───────────────────────────────────── */}
      {hasRecent && (
        <Group gap={4} style={{ flexShrink: 0 }}>
          <Button
            variant={view === 'recent' ? 'light' : 'subtle'}
            size="xs"
            radius="md"
            onClick={() => setView('recent')}
          >
            最近
          </Button>
          <Button
            variant={view === 'all' ? 'light' : 'subtle'}
            size="xs"
            radius="md"
            onClick={() => setView('all')}
          >
            全部
          </Button>
        </Group>
      )}

      {/* ── Emoji grid ─────────────────────────────────────────────────── */}
      <EmojiGrid items={gridItems} onSelect={handleSelect} />
    </>
  );

  // ── Popover variant: render inside a floating card shell ───────────────────
  if (variant === 'popover') {
    return (
      <Paper
        shadow="md"
        radius="lg"
        style={{
          display: 'flex',
          flexDirection: 'column',
          gap: 10,
          width: '100%',
          height: 'min(56vh, 400px)',
          padding: '12px 12px 10px',
          overflow: 'hidden',
          background: 'var(--locs-panel)',
          backdropFilter: 'blur(20px)',
          border: '1px solid var(--locs-border)',
          boxSizing: 'border-box',
        }}
      >
        {content}
      </Paper>
    );
  }

  // ── Inline variant: flush panel used in mobile drawer ─────────────────────
  return (
    <Box
      style={{
        display: 'flex',
        flexDirection: 'column',
        gap: 10,
        height: 260,
        padding: '8px 4px 4px',
        overflow: 'hidden',
        boxSizing: 'border-box',
      }}
    >
      {content}
    </Box>
  );
}
